import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { baseChart, tightGrid } from '../ChartFrame'
import { LiveChartFrame } from './LiveChartFrame'
import { CardShell } from './CardShell'
import { DailyResponse, Resource } from './types'
import { ChartKind } from './sections'
import { SERIES, themed, useChartTheme } from './chartTheme'
import { tooltipHtml } from './tooltip'
import { UNIT, compact } from './format'
import { useFormat, useT } from './i18n'

type Props = { daily: Resource<DailyResponse>; chartType: ChartKind }

const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

export function WeekdayCard({ daily, chartType }: Props) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.SECTION.WEEKDAYS')}
      subtitle={t('PROFILES_DASHBOARD.WEEKDAY.SUBTITLE', { unit: UNIT })}
      state={daily}
      empty={(d) => (d.kpis.complete_days ? null : t('PROFILES_DASHBOARD.COMMON.NO_COMPLETE_DAYS'))}
      render={(d) => <WeekdayChart data={d} chartType={chartType} />}
    />
  )
}

function WeekdayChart({ data, chartType }: { data: DailyResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const { items, highest, lowest } = data.weekdays
  const hasEvents = items.some((i) => i.event_days > 0)
  const byWeekday = (n: number | null) => items.find((i) => i.weekday === n)

  const options: ApexOptions = useMemo(
    () =>
      themed(
        {
          chart: { ...baseChart, type: chartType, toolbar: { show: false } },
          grid: tightGrid,
          plotOptions: {
            bar: { columnWidth: '55%', borderRadius: 3, dataLabels: { position: 'top' } },
          },
          stroke: { curve: 'straight', width: chartType === 'bar' ? 0 : 2.5 },
          // exact values on top of the bars: with a zero-based axis the differences between days are
          // small, so the numbers are what makes "which day is higher" readable
          dataLabels: {
            enabled: chartType === 'bar',
            offsetY: -18,
            style: { fontSize: '11px', colors: [theme.text] },
            formatter: (v) =>
              v === null || v === undefined
                ? ''
                : Math.abs(Number(v)) >= 10000
                ? compact(Number(v))
                : f.number(Number(v)),
          },
          xaxis: { categories: ISO_WEEKDAYS.map((n) => f.weekdayShort(n)) },
          yaxis: {
            title: { text: t('PROFILES_DASHBOARD.DAILY.Y_AXIS', { unit: UNIT }) },
            labels: { formatter: compact },
            min: 0,
          },
          colors: [SERIES.blue, SERIES.yellow],
          legend: { position: 'top', horizontalAlign: 'left', floating: false },
          tooltip: {
            shared: true,
            intersect: false,
            custom: ({ dataPointIndex }: any) => {
              const i = items[dataPointIndex]
              if (!i) return ''
              const rows = [
                {
                  label: t('PROFILES_DASHBOARD.WEEKDAY.TIP_NORMAL', { count: i.normal_days }),
                  value: f.kwh(i.normal_mean),
                  color: SERIES.blue,
                },
              ]
              if (i.event_days) {
                rows.push({
                  label: t('PROFILES_DASHBOARD.WEEKDAY.TIP_EVENT', { count: i.event_days }),
                  value: f.kwh(i.event_mean),
                  color: SERIES.yellow,
                })
              }
              rows.push({
                label: t('PROFILES_DASHBOARD.WEEKDAY.TIP_VS'),
                value: i.index === null ? '—' : f.percent((i.index - 1) * 100),
                color: undefined as unknown as string,
              })
              return tooltipHtml(theme, f.weekdayLong(i.weekday), rows)
            },
          },
        },
        theme
      ),
    [chartType, items, theme, t, f]
  )

  const series = useMemo(() => {
    const list = [
      { name: t('PROFILES_DASHBOARD.WEEKDAY.NORMAL'), data: items.map((i) => i.normal_mean) },
    ]
    if (hasEvents) {
      list.push({
        name: t('PROFILES_DASHBOARD.WEEKDAY.EVENT'),
        data: items.map((i) => i.event_mean),
      })
    }
    return list as ApexAxisChartSeries
  }, [items, hasEvents, t])

  const chip = (kind: 'HIGHEST' | 'LOWEST', weekday: number | null, tone: string) => {
    const item = byWeekday(weekday)
    if (!item || item.normal_mean === null) return null
    const values = {
      label: t(`PROFILES_DASHBOARD.WEEKDAY.CHIP_${kind}`),
      weekday: f.weekdayLong(item.weekday),
      value: f.kwh(item.normal_mean),
      vs: item.index !== null ? f.percent((item.index - 1) * 100) : '',
    }
    return (
      <span className={`badge badge-light-${tone}`}>
        {t(
          item.index !== null
            ? 'PROFILES_DASHBOARD.WEEKDAY.CHIP_WITH_INDEX'
            : 'PROFILES_DASHBOARD.WEEKDAY.CHIP',
          values
        )}
      </span>
    )
  }

  return (
    <>
      <div className='d-flex flex-wrap gap-2 mb-3'>
        {chip('HIGHEST', highest, 'danger')}
        {chip('LOWEST', lowest, 'primary')}
      </div>
      <LiveChartFrame>
        <ReactApexChart
          key={`weekdays-${data.meta.site.id}-${data.meta.meter_type}-${data.meta.start}-${data.meta.end}-${chartType}-${theme.mode}-${f.locale}`}
          options={options}
          series={series}
          type={chartType}
          width='100%'
          height={300}
        />
      </LiveChartFrame>
      <div className='fs-8 text-muted'>{t('PROFILES_DASHBOARD.WEEKDAY.FOOTNOTE')}</div>
    </>
  )
}
